import React, { useState, useEffect } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Zap, Code2, Sparkles, Trophy, Clock, Shield, 
  TrendingUp, Users, CheckCircle, ArrowRight, 
  Gift, Star, Heart, Rocket, Menu
} from 'lucide-react';
import sixthLogo from '../assets/icon.svg';

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  }
  
  html, body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    min-height: 100vh;
    background: #0a0a0f;
  }
`;

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(224, 5, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 20px rgba(224, 5, 255, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const float = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(5deg); 
  }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(224, 5, 255, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(224, 5, 255, 0.8);
  }
`;

// Navigation Header - same as pricing page
const NavigationHeader = styled.header`
  background: rgba(10, 10, 15, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(224, 5, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1rem 0;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 2rem;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  img {
    width: 32px;
    height: 32px;
    filter: drop-shadow(0 0 8px rgba(224, 5, 255, 0.5));
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  white-space: nowrap;
  
  &:hover {
    color: #E005FF;
    background: rgba(224, 5, 255, 0.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #E005FF, #A73CFF);
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 80%;
  }
  
  &.active {
    color: #E005FF;
    background: rgba(224, 5, 255, 0.15);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

// User Avatar
const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  margin-left: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(224, 5, 255, 0.3);
  }
`;

// Main container
const Container = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(224, 5, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(167, 60, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 40% 20%, rgba(120, 40, 200, 0.1) 0%, transparent 50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
`;

// Floating elements
const FloatingElement = styled.div`
  position: absolute;
  color: rgba(224, 5, 255, 0.3);
  font-size: 2rem;
  z-index: 0;
  ${props => css`
    animation: ${float} ${props.duration || '8s'} ease-in-out infinite;
    animation-delay: ${props.delay || '0s'};
  `}
`;

// Header Section
const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  z-index: 1;
  animation: ${fadeIn} 0.8s ease;
  max-width: 800px;
`;

const IconWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 2rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 53, 69, 0.1));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: ${pulse} 3s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(135deg, #FFB800, #FF6B6B);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #FFFFFF 0%, #FFB800 50%, #E005FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.03em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 auto 2rem;
  font-weight: 400;
  line-height: 1.6;
  max-width: 600px;
`;

// Special Offer Banner
const OfferBanner = styled.div`
  background: linear-gradient(135deg, rgba(255, 184, 0, 0.1), rgba(224, 5, 255, 0.1));
  border: 2px solid rgba(255, 184, 0, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;
  animation: ${glow} 2s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 184, 0, 0.2), transparent);
    animation: ${shimmer} 3s ease-in-out infinite;
  }
`;

const OfferTag = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const OfferText = styled.p`
  font-size: 1.1rem;
  color: #FFFFFF;
  margin: 0;
  font-weight: 600;
  position: relative;
  z-index: 1;
  
  span {
    color: #FFB800;
    font-weight: 800;
  }
`;

// Value Props Grid
const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  width: 100%;
  margin-bottom: 3rem;
  z-index: 1;
`;

const ValueCard = styled.div`
  background: rgba(18, 18, 25, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(224, 5, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.3s ease;
  animation: ${slideUp} 0.6s ease;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(224, 5, 255, 0.3);
    box-shadow: 0 20px 40px rgba(224, 5, 255, 0.2);
  }
`;

const ValueIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(224, 5, 255, 0.1), rgba(167, 60, 255, 0.1));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #E005FF;
  
  svg {
    width: 28px;
    height: 28px;
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 0.75rem;
`;

const ValueDescription = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
`;

// Testimonial Section
const TestimonialSection = styled.div`
  max-width: 800px;
  width: 100%;
  margin-bottom: 3rem;
  z-index: 1;
`;

const TestimonialCard = styled.div`
  background: linear-gradient(135deg, rgba(224, 5, 255, 0.05), rgba(167, 60, 255, 0.05));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(224, 5, 255, 0.2);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  position: relative;
  animation: ${fadeIn} 0.8s ease;
  animation-delay: 0.6s;
  animation-fill-mode: both;
`;

const QuoteIcon = styled.div`
  font-size: 3rem;
  color: rgba(224, 5, 255, 0.3);
  margin-bottom: 1rem;
`;

const TestimonialText = styled.p`
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
`;

const AuthorInfo = styled.div`
  text-align: left;
`;

const AuthorName = styled.div`
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
`;

// Feature List
const FeatureList = styled.div`
  max-width: 600px;
  width: 100%;
  margin-bottom: 3rem;
  z-index: 1;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(18, 18, 25, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(224, 5, 255, 0.1);
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  animation: ${slideUp} 0.6s ease;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  
  &:hover {
    background: rgba(224, 5, 255, 0.05);
    border-color: rgba(224, 5, 255, 0.3);
    transform: translateX(5px);
  }
`;

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #10B981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FeatureText = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
`;

// CTA Section
const CTASection = styled.div`
  text-align: center;
  z-index: 1;
  margin-bottom: 2rem;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #E005FF 0%, #6D28D9 100%);
  border: none;
  border-radius: 16px;
  color: #FFFFFF;
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  padding: 1.25rem 3rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 10px 30px rgba(224, 5, 255, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #6D28D9 0%, #E005FF 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(224, 5, 255, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  span, svg {
    position: relative;
    z-index: 1;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.75rem 2rem;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(224, 5, 255, 0.5);
    color: #E005FF;
    background: rgba(224, 5, 255, 0.05);
  }
`;

// Stats Section
const StatsSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 3rem;
  z-index: 1;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.8s ease;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #E005FF, #A73CFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Footer Styles
const Footer = styled.footer`
  width: 100%;
  background: rgba(18, 18, 25, 0.8);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(224, 5, 255, 0.1);
  padding: 3rem 1rem;
  margin-top: 4rem;
  z-index: 1;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h4`
  color: #FFFFFF;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FooterLink = styled.a`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s ease;
  font-size: 0.95rem;
  
  &:hover {
    color: #E005FF;
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(224, 5, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
`;

const PaymentCancelled = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userInitial, setUserInitial] = useState('');

  useEffect(() => {
    // Check authentication status
    const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');
    const email = localStorage.getItem('email');
    
    if (token && email) {
      setIsAuthenticated(true);
      setUserEmail(email);
      setUserInitial(email.charAt(0).toUpperCase());
    }
  }, []);

  const handleRetryPayment = () => {
    navigate('/pricing');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@trysixth.com';
  };

  return (
    <>
      <GlobalStyle />
      <Container>
       <Header>
                 <Nav>
                   <Logo to="/">
                     <img src={sixthLogo} alt="Sixth AI" />
                     Sixth AI
                   </Logo>
                   <NavLinks>
                     {isAuthenticated ? (
                       // Show for authenticated users
                       <>
                         <NavLink to={`/vscode/dashboard`}>Dashboard</NavLink>
                         <NavLink to={`/pricing}`} className="active">Pricing</NavLink>
                         <UserAvatar title={userEmail || 'User'}>
                           {userInitial}
                         </UserAvatar>
                       </>
                     ) : (
                       // Show for non-authenticated users
                       <>
                         <NavLink to={`/vscode/signin`}>Sign In</NavLink>
                         <NavLink to={`/vscode/signup`}>Sign Up</NavLink>
                         <NavLink to={`/pricing`} className="active">Pricing</NavLink>
                       </>
                     )}
                   </NavLinks>
                   <MobileMenuButton>
                     <Menu size={24} />
                   </MobileMenuButton>
                 </Nav>
               </Header>
        {/* Floating background elements */}
        <FloatingElement style={{ top: '10%', left: '5%' }} duration="8s">
          <Sparkles />
        </FloatingElement>
        <FloatingElement style={{ top: '20%', right: '10%' }} duration="10s" delay="1s">
          <Code2 />
        </FloatingElement>
        <FloatingElement style={{ bottom: '30%', left: '10%' }} duration="7s" delay="2s">
          <Zap />
        </FloatingElement>
        <FloatingElement style={{ bottom: '15%', right: '5%' }} duration="9s" delay="1.5s">
          <Trophy />
        </FloatingElement>

        <Header>
          <IconWrapper>
            <Gift size={50} color="#FFB800" />
          </IconWrapper>
          <Title>Wait! Your Upgrade is Still Available</Title>
          <Subtitle>
            Don't miss out on transforming your development workflow. 
            Join thousands of developers who are already coding 10x faster with Sixth AI.
          </Subtitle>
        </Header>

        {/* Special Offer Banner */}
        <OfferBanner>
          <OfferTag>Still Available</OfferTag>
          <OfferText>
            Your selected plan is waiting for you. Join developers who are already 
            <span> saving 20+ hours every week</span> with intelligent AI assistance.
          </OfferText>
        </OfferBanner>

        {/* CTA Section - Moved up */}
        <CTASection>
          <CTAButton onClick={handleRetryPayment}>
            <span>Continue to Pricing</span>
            <ArrowRight size={24} />
          </CTAButton>
          <br />
          <SecondaryButton onClick={handleContactSupport}>
            Questions? Chat with our team
          </SecondaryButton>
        </CTASection>

        {/* Stats Section */}
        <StatsSection>
          <StatItem delay="0.2s">
            <StatNumber>10x</StatNumber>
            <StatLabel>Faster Coding</StatLabel>
          </StatItem>
          <StatItem delay="0.3s">
            <StatNumber>50K+</StatNumber>
            <StatLabel>Active Users</StatLabel>
          </StatItem>
          <StatItem delay="0.4s">
            <StatNumber>4.9/5</StatNumber>
            <StatLabel>User Rating</StatLabel>
          </StatItem>
          <StatItem delay="0.5s">
            <StatNumber>24/7</StatNumber>
            <StatLabel>AI Support</StatLabel>
          </StatItem>
        </StatsSection>

        {/* Value Props */}
        <ValueGrid>
          <ValueCard delay="0.2s">
            <ValueIcon>
              <Rocket />
            </ValueIcon>
            <ValueTitle>Ship 10x Faster</ValueTitle>
            <ValueDescription>
              Our AI understands your entire codebase and writes production-ready code in seconds, 
              not hours.
            </ValueDescription>
          </ValueCard>

          <ValueCard delay="0.3s">
            <ValueIcon>
              <Shield />
            </ValueIcon>
            <ValueTitle>Enterprise-Grade Security</ValueTitle>
            <ValueDescription>
              Your code never leaves your machine. SOC 2 compliant with end-to-end encryption.
            </ValueDescription>
          </ValueCard>

          <ValueCard delay="0.4s">
            <ValueIcon>
              <TrendingUp />
            </ValueIcon>
            <ValueTitle>Guaranteed ROI</ValueTitle>
            <ValueDescription>
              Save 20+ hours per week. If you don't see results in 30 days, get a full refund.
            </ValueDescription>
          </ValueCard>

          <ValueCard delay="0.5s">
            <ValueIcon>
              <Users />
            </ValueIcon>
            <ValueTitle>Priority Support</ValueTitle>
            <ValueDescription>
              Get instant help from our team of experts. Average response time under 2 minutes.
            </ValueDescription>
          </ValueCard>

          <ValueCard delay="0.6s">
            <ValueIcon>
              <Sparkles />
            </ValueIcon>
            <ValueTitle>Latest AI Models</ValueTitle>
            <ValueDescription>
              Always get access to the newest, most powerful AI models including GPT-5.1 and Claude Opus.
            </ValueDescription>
          </ValueCard>

          <ValueCard delay="0.7s">
            <ValueIcon>
              <Clock />
            </ValueIcon>
            <ValueTitle>Unlimited Usage</ValueTitle>
            <ValueDescription>
              No token limits, no restrictions. Code as much as you want, whenever you want.
            </ValueDescription>
          </ValueCard>
        </ValueGrid>

        {/* Features List */}
        <FeatureList>
          <FeatureItem delay="0.3s">
            <FeatureIcon>
              <CheckCircle size={16} />
            </FeatureIcon>
            <FeatureText>Intelligent code completion that understands context</FeatureText>
          </FeatureItem>
          <FeatureItem delay="0.4s">
            <FeatureIcon>
              <CheckCircle size={16} />
            </FeatureIcon>
            <FeatureText>Automatic bug detection and fixing</FeatureText>
          </FeatureItem>
          <FeatureItem delay="0.5s">
            <FeatureIcon>
              <CheckCircle size={16} />
            </FeatureIcon>
            <FeatureText>Code review and optimization suggestions</FeatureText>
          </FeatureItem>
          <FeatureItem delay="0.6s">
            <FeatureIcon>
              <CheckCircle size={16} />
            </FeatureIcon>
            <FeatureText>Natural language to code generation</FeatureText>
          </FeatureItem>
          <FeatureItem delay="0.7s">
            <FeatureIcon>
              <CheckCircle size={16} />
            </FeatureIcon>
            <FeatureText>Works with all major programming languages</FeatureText>
          </FeatureItem>
        </FeatureList>

        {/* Testimonial */}
        <TestimonialSection>
          <TestimonialCard>
            <QuoteIcon>"</QuoteIcon>
            <TestimonialText>
              "Sixth AI literally changed my life. I went from spending 60 hours a week coding to 30 hours, 
              while shipping twice as much. It's like having a senior developer pair programming with me 24/7."
            </TestimonialText>
            <TestimonialAuthor>
              <AuthorAvatar>JD</AuthorAvatar>
              <AuthorInfo>
                <AuthorName>John Davidson</AuthorName>
                <AuthorRole>Senior Developer at Microsoft</AuthorRole>
              </AuthorInfo>
            </TestimonialAuthor>
          </TestimonialCard>
        </TestimonialSection>

        {/* Footer */}
        <Footer>
          <FooterContent>
            <FooterSection>
              <FooterTitle>Product</FooterTitle>
              <FooterLinks>
                <FooterLink href="/pricing">Pricing</FooterLink>
                <FooterLink href="/vscode/dashboard">Dashboard</FooterLink>
                <FooterLink href="https://docs.trysixth.com" target="_blank">Documentation</FooterLink>
                <FooterLink href="https://marketplace.visualstudio.com/items?itemName=sixth.sixth-ai" target="_blank">VS Code Extension</FooterLink>
              </FooterLinks>
            </FooterSection>
            
            <FooterSection>
              <FooterTitle>Support</FooterTitle>
              <FooterLinks>
                <FooterLink href="mailto:support@trysixth.com">Email Support</FooterLink>
                <FooterLink href="https://discord.gg/sixth" target="_blank">Discord Community</FooterLink>
                <FooterLink href="https://trysixth.com/faq" target="_blank">FAQ</FooterLink>
                <FooterLink href="https://github.com/trysixth" target="_blank">GitHub</FooterLink>
              </FooterLinks>
            </FooterSection>
            
            <FooterSection>
              <FooterTitle>Legal</FooterTitle>
              <FooterLinks>
                <FooterLink href="https://trysixth.com/privacy" target="_blank">Privacy Policy</FooterLink>
                <FooterLink href="https://trysixth.com/terms" target="_blank">Terms of Service</FooterLink>
                <FooterLink href="https://trysixth.com/security" target="_blank">Security</FooterLink>
                <FooterLink href="https://trysixth.com/refund" target="_blank">Refund Policy</FooterLink>
              </FooterLinks>
            </FooterSection>
          </FooterContent>
          
          <FooterBottom>
            <p>© 2024 Sixth AI. All rights reserved. | Built with ❤️ for developers</p>
          </FooterBottom>
        </Footer>
      </Container>
    </>
  );
};

export default PaymentCancelled;